import type React from 'react'
import { Badge, BadgeVariant } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export const COLOR_OPTIONS = [
  'default', 'blue', 'green', 'red', 'orange', 'purple',
  'yellow', 'cyan', 'pink', 'indigo', 'teal', 'gray',
] as const
export type ColorOption = typeof COLOR_OPTIONS[number]

export const COLOR_BADGE_VARIANT_MAP: Record<ColorOption, BadgeVariant> = {
  default: BadgeVariant.SECONDARY,
  blue: BadgeVariant.DEFAULT,
  green: BadgeVariant.SUCCESS,
  red: BadgeVariant.DESTRUCTIVE,
  orange: BadgeVariant.WARNING,
  purple: BadgeVariant.SECONDARY,
  yellow: BadgeVariant.WARNING,
  cyan: BadgeVariant.DEFAULT,
  pink: BadgeVariant.SECONDARY,
  indigo: BadgeVariant.DEFAULT,
  teal: BadgeVariant.SUCCESS,
  gray: BadgeVariant.OUTLINE,
}

export const COLOR_BADGE_CLASSNAME_MAP: Record<ColorOption, string> = {
  default: '',
  blue: 'bg-blue-500 text-white border-transparent hover:bg-blue-500/80',
  green: 'bg-green-500 text-white border-transparent hover:bg-green-500/80',
  red: 'bg-red-500 text-white border-transparent hover:bg-red-500/80',
  orange: 'bg-orange-500 text-white border-transparent hover:bg-orange-500/80',
  purple: 'bg-purple-500 text-white border-transparent hover:bg-purple-500/80',
  yellow: 'bg-yellow-400 text-yellow-900 border-transparent hover:bg-yellow-400/80',
  cyan: 'bg-cyan-500 text-white border-transparent hover:bg-cyan-500/80',
  pink: 'bg-pink-500 text-white border-transparent hover:bg-pink-500/80',
  indigo: 'bg-indigo-500 text-white border-transparent hover:bg-indigo-500/80',
  teal: 'bg-teal-500 text-white border-transparent hover:bg-teal-500/80',
  gray: 'bg-gray-400 text-white border-transparent hover:bg-gray-400/80',
}

export function resolveEntityTypeColorVariant(color: string | null | undefined): BadgeVariant {
  if (!color) return BadgeVariant.SECONDARY
  return COLOR_BADGE_VARIANT_MAP[color as ColorOption] ?? BadgeVariant.SECONDARY
}

export interface EntityTypeBadgeProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  color?: string | null
  children: React.ReactNode
  className?: string
}

export function EntityTypeBadge({ color, children, className, ...rest }: EntityTypeBadgeProps) {
  // eslint-disable-next-line i18next/no-literal-string -- 'default' is a technical constant from COLOR_OPTIONS, not a UI string
  const colorKey = (COLOR_OPTIONS as readonly string[]).includes(color ?? '') ? color as ColorOption : 'default'
  return (
    <Badge
      variant={COLOR_BADGE_VARIANT_MAP[colorKey]}
      className={cn(COLOR_BADGE_CLASSNAME_MAP[colorKey], className)}
      {...rest}
    >
      {children}
    </Badge>
  )
}
